import { useEffect, useMemo, useState } from 'react';
import Header from '../components/Header.jsx';
import DailyScore from '../components/DailyScore.jsx';
import PersonaToggle from '../components/PersonaToggle.jsx';
import WeatherOverview from '../components/WeatherOverview.jsx';
import TonightInTown from '../components/TonightInTown.jsx';
import UpcomingWeek from '../components/UpcomingWeek.jsx';
import PicksAndAvoid from '../components/PicksAndAvoid.jsx';
import BeachConditions from '../components/BeachConditions.jsx';
import TideChart from '../components/TideChart.jsx';
import DiningTonight from '../components/DiningTonight.jsx';
import MarineConditions from '../components/MarineConditions.jsx';
import BoatRamps from '../components/BoatRamps.jsx';
import TravelIn from '../components/TravelIn.jsx';
import HostNote from '../components/HostNote.jsx';
import DayStrip from '../components/DayStrip.jsx';
import JumpNav from '../components/JumpNav.jsx';
import { Eyebrow } from '../components/Section.jsx';
import Footer from '../components/Footer.jsx';
import Seo from '../components/Seo.jsx';
import NotFound from '../components/NotFound.jsx';
import { Link } from 'react-router-dom';
import { fetchBrief, selectPersonaContent } from '../lib/data.js';
import { planDayparts } from '../lib/plan.js';
import { getPersona, loadStoredPersona, saveStoredPersona } from '../lib/personas.js';
import { composeBriefDescription, etToday, formatShortDate, isStale, STALE_HOURS } from '../lib/format.js';
import { briefArticleJsonLd, websiteJsonLd } from '../lib/brief-schema.js';
import { canonicalOrigin, canonicalUrl } from '../lib/brand.js';

export default function Today({ briefPath = '/api/get-brief', archiveLabel, seoTitle }) {
  const [brief, setBrief] = useState(null);
  const [error, setError] = useState(null);
  const [personaId, setPersonaId] = useState(loadStoredPersona);

  useEffect(() => {
    setError(null);
    fetchBrief(briefPath).then(setBrief).catch(setError);
  }, [briefPath]);

  const persona = useMemo(() => getPersona(personaId), [personaId]);
  const personaContent = useMemo(() => selectPersonaContent(brief, personaId), [brief, personaId]);

  function handlePersonaChange(id) {
    setPersonaId(id);
    saveStoredPersona(id);
  }

  // A date with no brief is a missing page, not a broken one. 400 lands here
  // too: /api/get-brief now rejects a malformed date outright.
  if (error) {
    return error.status === 404 || error.status === 400 ? <MissingBrief /> : <ErrorState />;
  }
  if (!brief) return <LoadingState />;

  const stale = !archiveLabel && isStale(brief.generatedAt);
  const isHome = !archiveLabel;
  // "Today" rolls over at midnight ET but the new brief lands in the 6 AM
  // hour (5 AM once the clocks go back), so
  // between those the live page is honestly showing yesterday's brief.
  const isBriefToday = brief.date === etToday();
  const seoDescription = composeBriefDescription(brief);
  // One story when the brief meets the plan's contract: the picks become the
  // plan's daypart subheads and the lead drops its own list (src/lib/plan.js).
  // The lead (headline and score) is the same for every persona, so it sits
  // above the persona toggle, and the tailored plan sits right under it,
  // where a tap on a pill changes what the guest is looking at.
  const dayparts = planDayparts(
    personaContent?.hostNote,
    personaContent?.topPicks || brief.score?.topPicks
  );

  // Every brief page describes itself as a dated NewsArticle; the home page
  // also carries the WebSite block, which is about the site rather than the
  // day. One array under one marker, matching what the prerender bakes in, so
  // the client-side block replaces the static one instead of joining it.
  const origin = canonicalOrigin();
  const article = briefArticleJsonLd(brief, canonicalUrl(), origin);
  const blocks = [isHome ? websiteJsonLd(origin) : null, article].filter(Boolean);
  const jsonLd = blocks.length ? blocks : null;

  return (
    <div className="mx-auto max-w-brief px-4 pb-20 pt-6 sm:px-6 sm:pb-0">
      <Seo
        title={
          seoTitle ||
          `Charleston.Vacations · Today's Brief · ${formatShortDate(brief.date)}`
        }
        description={seoDescription}
        jsonLd={jsonLd}
      />
      {/* The tide clock and the moon beside the nameplate keep the present
          hour, so only today's edition of the live page carries them; an
          archive day, or yesterday's brief before the morning publish, has
          no "now" to set the clock by. */}
      <Header
        date={brief.date}
        generatedAt={brief.generatedAt}
        ears={
          isHome && isBriefToday
            ? {
                tides: brief.tides,
                sunrise: brief.weatherDetails?.sunrise,
                sunset: brief.weatherDetails?.sunset,
                moon: brief.weatherDetails?.moon
              }
            : null
        }
      />
      {stale && (
        <div className="mt-4 rounded-lg border border-charlestonpink-200 bg-charlestonpink-50 px-4 py-3 text-sm text-charlestonpink-600">
          This brief hasn't refreshed in over {STALE_HOURS} hours. Conditions may have shifted.
        </div>
      )}
      {/* The archive label is the lead's own kicker, not a box: the dateline
          directly above already states the date, so the label only has to say
          which edition this is (design review 2026-09-26, finding 5). It
          stands alone only if a brief ever arrives without a score. */}
      {archiveLabel && !brief.score && (
        <div className="mt-4">
          <Eyebrow text={archiveLabel} />
        </div>
      )}
      <DailyScore
        score={brief.score}
        date={brief.date}
        isToday={isBriefToday}
        kicker={archiveLabel}
        showPicks={!dayparts}
        personaTopPicks={personaContent?.topPicks}
        personaLabel={persona.label}
        personaId={personaId}
      />
      <PersonaToggle value={personaId} onChange={handlePersonaChange} />
      <HostNote
        text={personaContent?.hostNote}
        dayparts={dayparts}
        personaLabel={persona.label}
        strip={
          <DayStrip
            tides={brief.tides}
            sunrise={brief.weatherDetails?.sunrise}
            sunset={brief.weatherDetails?.sunset}
            moon={brief.weatherDetails?.moon}
            live={isHome && isBriefToday}
          />
        }
      />
      <SectionStack
        brief={brief}
        persona={persona}
        personaContent={personaContent}
      />
      <Footer />
      <JumpNav />
    </div>
  );
}

function SectionStack({ brief, persona, personaContent }) {
  const renderers = {
    weather: () => (
      <WeatherOverview
        summary={brief.weatherSummary}
        details={brief.weatherDetails}
        forecast={brief.forecast}
      />
    ),
    tonight: () => <TonightInTown tonight={brief.tonightInTown} />,
    week: () => <UpcomingWeek events={brief.upcomingEvents} />,
    picks: () => <PicksAndAvoid items={personaContent?.picksAndAvoid} />,
    beach: () => <BeachConditions beachNotes={brief.beachNotes} />,
    tide: () => <TideChart tides={brief.tides} />,
    dining: () => <DiningTonight picks={personaContent?.diningTonight} />,
    marine: () => (
      <MarineConditions
        note={brief.marineNote}
        buoys={brief.buoys}
        defaultOpen={persona.expanded.marine}
      />
    ),
    ramps: () => <BoatRamps note={brief.boatRampNote} defaultOpen={persona.expanded.ramps} />,
    travel: () => <TravelIn note={brief.travelInNote} />
  };

  return (
    <div>
      {persona.sectionOrder.map((id) => {
        const Renderer = renderers[id];
        if (!Renderer) return null;
        return <div key={id}>{Renderer()}</div>;
      })}
    </div>
  );
}

function LoadingState() {
  // Skeleton mirroring the header + front-page lead (kicker, two-line
  // headline, deck, the ear) + persona toggle + the plan's first daypart, so
  // the page doesn't jump when the brief lands. The ear is a ruled row on
  // phones and a column beside the headline at sm+, as in DailyScore. It
  // follows the merged plan, the shape nearly every brief takes.
  return (
    <div className="mx-auto max-w-brief px-4 pt-6 sm:px-6" aria-busy="true">
      <div className="animate-pulse motion-reduce:animate-none">
        {/* Compact masthead on phones (no tagline), full at sm+, as Header. */}
        <div className="mx-auto mt-2 h-8 w-10 rounded bg-navy-100 sm:h-[60px]" />
        <div className="mx-auto mt-2 h-8 w-72 max-w-full rounded bg-navy-100 sm:h-[46px] sm:w-[400px]" />
        <div className="mx-auto mt-3 hidden h-3 w-52 rounded bg-navy-100 sm:block" />
        <div className="mt-3 h-9 rounded bg-navy-100 sm:mt-5" />
        <div className="mt-4 flex items-center justify-between">
          <div className="h-3 w-48 rounded bg-navy-100" />
          <div className="h-3 w-32 rounded bg-navy-100" />
        </div>
        <div className="pb-4 pt-4 sm:grid sm:grid-cols-[minmax(0,1fr)_176px] sm:gap-9 sm:pb-8 sm:pt-7">
          <div>
            <div className="h-3 w-28 rounded bg-navy-100" />
            <div className="mt-3 h-8 rounded bg-navy-100 sm:h-11" />
            <div className="mt-2 h-8 w-2/3 rounded bg-navy-100 sm:h-11" />
            <div className="mt-3 h-4 w-3/4 rounded bg-navy-100" />
          </div>
          <div className="mt-4 h-[74px] bg-navy-100 sm:mt-1 sm:h-[150px]" />
        </div>
        <div className="h-[60px] rounded bg-navy-100 sm:h-24" />
        <div className="mt-8 border-t border-navy-100 pt-6">
          <div className="h-3 w-20 rounded bg-navy-100" />
          <div className="mt-3 h-7 w-56 rounded bg-navy-100" />
          <div className="mt-4 sm:grid sm:grid-cols-[7rem_minmax(0,1fr)] sm:gap-6">
            <div className="h-3 w-16 rounded bg-navy-100" />
            <div>
              <div className="mt-2 h-6 w-4/5 rounded bg-navy-100 sm:mt-0" />
              <div className="mt-3 h-24 rounded bg-navy-100" />
            </div>
          </div>
        </div>
      </div>
      <p className="sr-only">Loading today's brief…</p>
    </div>
  );
}

// No brief under that date. The guide and stay routes already had a proper
// not-found state; the brief routes answered with the homepage's title, no
// noindex, and the string "Failed to load brief (404)" on the page.
function MissingBrief() {
  return (
    <NotFound title="No brief for that date." seoTitle="No brief here · Charleston.Vacations">
      The archive starts in late April 2026 and runs to yesterday.{' '}
      <Link to="/" className="text-navy-700 underline">Today's brief</Link> is always
      current, and{' '}
      <Link to="/yesterday" className="text-navy-700 underline">yesterday's</Link> is
      one click back.
    </NotFound>
  );
}

// Something went wrong at our end, which is a different page from a date that
// has no brief. Still no status code: a number is not an instruction.
function ErrorState() {
  return (
    <NotFound title="Couldn't load the brief." seoTitle="Brief unavailable · Charleston.Vacations">
      Something went wrong at our end. Refreshing usually fixes it; if it
      doesn't,{' '}
      <Link to="/" className="text-navy-700 underline">start from today's brief</Link>.
    </NotFound>
  );
}
